import React, { useEffect, useRef, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Toolbar from '@material-ui/core/Toolbar';
import AppBar from '@material-ui/core/AppBar';
import { Button, Link, Menu, MenuItem } from '@material-ui/core';
import { useAuth } from 'react-oidc-context';

import { useDOPermissions } from '../../core/auth';

const mode = import.meta.env.VITE_DO_MODE;

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
  },
  menuButton: {
    marginRight: theme.spacing(2),
  },
  title: {
    flexGrow: 1,
  },
  appBar: {
    backgroundColor: mode === 'vision' ? theme.palette.primary[700] : 'black',
  },
  logo: {
    maxHeight: '50px',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  toolBar: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  button: { minWidth: 'fit-content' },
  navigation: {
    display: 'flex',
    gap: '3rem',
    width: '100%',
    marginLeft: '5rem',
  },
  link: {
    color: 'white',
    fontSize: '0.95rem',
  },
  userMenuBtn: {
    height: 'calc(4rem - 2px)',
    padding: '0 0.5rem 0 0.5rem',
    marginRight: '1.5rem',
    color: 'white',
    borderRadius: 0,
  },
  nameInitials: {
    borderRadius: '50%',
    width: '48px',
    height: '48px',
    backgroundColor: '#aaacb3',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));
export interface PrivateLayoutProps extends RouteComponentProps<{}> {
  children: React.ReactNode;
}

export const PrivateLayout: React.FC<PrivateLayoutProps> = props => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const prevLocation = useRef('');
  const { signoutRedirect } = useAuth();
  const {
    isAuthenticated,
    isAdmin,
    canCreateDigitalOrder,
    initials,
    profileUrl,
  } = useDOPermissions();
  const mode = import.meta.env.VITE_DO_MODE || 'matrix';
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    if (prevLocation.current !== props.location.pathname) {
      window.scrollTo(0, 0);
      prevLocation.current = props.location.pathname;
    }
  }, [props.location]);

  const handleLogout = () => {
    void signoutRedirect();
  };

  const handleProfile = () => {
    if (!profileUrl) {
      console.log('missing profile path');
      return;
    }
    try {
      const i18nLang = localStorage.getItem('I18_NEXT_LNG');
      const userLang = i18nLang ? `?ui_locales=${i18nLang}` : '';
      window.open(profileUrl + userLang, '_blank', 'noopener,noreferrer');
    } catch (e) {
      console.log(e);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setUserMenuOpen(true);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setUserMenuOpen(false);
  };

  return (
    <>
      <AppBar position="fixed" className={classes.appBar}>
        <Toolbar className={classes.toolBar}>
          {mode === 'vision' ? (
            <img src="/visionLogo.svg" alt="Vision Fitness"></img>
          ) : (
            <img
              className={classes.logo}
              src={'https://images.jhtassets.com/6747ec2d9c74a0ac9b16e33e5a97e571aecac1fc/'}
              alt="Matrix"
            ></img>
          )}
          <Toolbar className={classes.navigation}>
            {canCreateDigitalOrder && (
              <Link href="/digital-orders" underline="none" className={classes.link}>
                Start Order
              </Link>
            )}
            {isAdmin && (
              <Link
                href="/manage-orders?status=Submitted"
                underline="none"
                className={classes.link}
              >
                Manage Orders
              </Link>
            )}
          </Toolbar>
          {isAuthenticated && (
            <div>
              <Button
                className={classes.userMenuBtn}
                onClick={handleMenuOpen}
                aria-controls="user-menu"
                aria-haspopup="true"
              >
                <span className={classes.nameInitials}>{initials}</span>
              </Button>
              <Menu
                id="user-menu"
                anchorEl={anchorEl}
                open={userMenuOpen}
                onClose={handleMenuClose}
                getContentAnchorEl={null}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
              >
                <MenuItem
                  onClick={() => {
                    handleProfile();
                    setUserMenuOpen(false);
                  }}
                >
                  Profile
                </MenuItem>
                <MenuItem onClick={handleLogout}>Log Out</MenuItem>
              </Menu>
            </div>
          )}
        </Toolbar>
      </AppBar>
      <Toolbar />
      {props.children}
    </>
  );
};

export default PrivateLayout;
